import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { HERO_PORTRAIT, HERO_DETAIL } from "@/lib/constants";

export default function Hero() {
  return (
    <section data-testid="home-hero" className="relative pt-36 lg:pt-44 pb-12 lg:pb-16 bg-ivory grain overflow-hidden">
      <div className="container-editorial">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left — text */}
          <div className="lg:col-span-6 animate-fade-up">
            <p className="overline mb-8" data-testid="hero-overline">
              Clarity · Structure · Strategy · Stability
            </p>
            <h1 className="font-serif font-medium text-navy leading-[1.02] tracking-[-0.015em] text-[2.75rem] md:text-6xl lg:text-[4.75rem]">
              Better Systems.<br/>
              Stronger Bookings.<br/>
              More{" "}
              <span className="relative inline-block">
                <span className="font-script text-sage text-6xl md:text-7xl lg:text-[5.5rem] leading-none align-baseline">Freedom.</span>
                <span className="absolute left-1 right-1 bottom-2 h-[5px] bg-gold/65 -z-0 rounded-[1px]" />
              </span>
            </h1>

            <p className="mt-10 font-sans text-base md:text-lg text-charcoal/70 leading-[1.75] max-w-xl">
              I help Airbnb owners identify what's quietly holding their property back — and create a clear,
              measurable strategy that improves performance, reduces overwhelm, and builds lasting financial stability.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link to="/contact?type=audit" data-testid="hero-cta-audit" className="btn-gold group">
                Get Your Revenue Leak Audit
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="btn-outline">
                Explore Services
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-5">
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=80&h=80&q=80",
                  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=80&h=80&q=80",
                  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=80&h=80&q=80",
                  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=80&h=80&q=80",
                ].map((src, i) => (
                  <img key={i} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-ivory object-cover" />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-gold">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
                </div>
                <p className="font-sans text-[13px] text-charcoal/70 mt-2 tracking-wide">Trusted by Airbnb owners &amp; investors across the country.</p>
              </div>
            </div>
          </div>

          {/* Right — asymmetric editorial portrait + detail */}
          <div className="lg:col-span-6 relative animate-fade-in" data-testid="hero-image-block">
            <div className="relative">
              {/* Primary: Sarah portrait */}
              <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden">
                <img
                  src={HERO_PORTRAIT}
                  alt="Sarah Atkinson"
                  className="w-full h-full object-cover object-[center_top]"
                  loading="eager"
                />
              </div>

              {/* Layered secondary detail — bottom left */}
              <div className="hidden md:block absolute -left-12 lg:-left-16 -bottom-10 w-44 lg:w-52 aspect-[4/5] overflow-hidden shadow-[0_24px_48px_-24px_rgba(13,27,52,0.35)] border-4 border-ivory">
                <img src={HERO_DETAIL} alt="A quiet workspace detail" className="w-full h-full object-cover" />
              </div>

              {/* Editorial caption — top right (replaces script badge) */}
              <div className="hidden md:block absolute -right-2 lg:-right-6 top-8">
                <div className="bg-ivory/95 backdrop-blur-sm px-6 py-5 border-l-2 border-gold max-w-[210px]">
                  <p className="font-ui uppercase tracking-[0.18em] text-[12px] font-semibold text-sage mb-2">Brand Truth</p>
                  <p className="font-serif italic text-navy text-[17px] leading-snug">
                    Clarity creates confidence.
                  </p>
                </div>
              </div>
            </div>

            {/* Issue / volume label — subtle magazine touch */}
            <div className="hidden lg:flex absolute -right-2 bottom-6 items-center gap-3">
              <span className="block w-10 h-px bg-navy/35" />
              <span className="font-ui uppercase tracking-[0.22em] text-[12px] font-semibold text-charcoal/65">
                Vol. 01 · Strategic Advisory
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
